import { useEffect, useMemo, useState } from 'react';

import { useSession } from '@/lib/auth/session';
import { MENU_EXTERNAL_LINKS, menuKeysOf, mergeMenuConfig, type MenuConfigItem } from '@/lib/menu/menuConfig';
import { closedKeysFor } from '@/lib/menu/menuGate';
import { orderByKeys, resolveSiteFirst, SITE_FIRST_MENU_ORDER } from '@/lib/menu/siteFirst';
import { isRecent, useMenuActivity } from '@/lib/menu/useMenuActivity';
import { useMenuSettings } from '@/lib/menu/useMenuSettings';
import { useExternalLinkUrls } from '@/lib/settings/useExternalLinks';
import { isAdministrator } from '@/lib/types';

/**
 * 사용자에게 보여줄 메뉴 트리.
 *
 * 사이드바와 홈 위젯이 <b>같은 트리</b>를 봐야 한다. 메뉴가 하나 늘었는데 한쪽에만 나타나거나,
 * 권한 필터가 한쪽에만 걸리면 그게 곧 사고다. 그래서 병합·필터·정렬·배지 판정을 여기 모은다.
 *
 * 세 개의 쿼리를 쓰지만 모두 캐시 키를 공유하므로, 이 훅을 여러 화면에서 불러도 요청은 한 번씩만 나간다.
 */

/** "24시간 이내" 경계를 시간이 흘러도 갱신하기 위한 주기. */
const BADGE_TICK_MS = 60_000;

export type MenuTree = {
  /** 그룹 안에 든 것을 제외한 최상위 항목. 자식이 모두 가려진 빈 그룹은 빠진다. */
  topLevel: MenuConfigItem[];
  childrenOf: (menuKey: string) => MenuConfigItem[];
  /** 자신이나 하위 어딘가가 최근에 바뀌었는가. */
  hasBadge: (menuKey: string) => boolean;
  /** 현재 경로가 이 항목(또는 하위)에 해당하는가. */
  isActive: (item: MenuConfigItem, pathname: string) => boolean;
};

export function useMenuTree(): MenuTree {
  const { data: session } = useSession();
  const { data: menuSettings } = useMenuSettings();
  const { data: menuActivity } = useMenuActivity();
  /**
   * 바깥으로 나가는 줄의 주소(사용자 지시 2026-09-01). 응답 전에는 빈 객체라 골격의 기본값이 그대로 선다 —
   * 그래야 링크가 «잠깐 아무 데도 가지 않는» 순간이 없다.
   */
  const externalLinkUrls = useExternalLinkUrls();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), BADGE_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const menuConfig = useMemo(() => {
    // 로그인한 세션을 손에 넣기 전에는 아무 메뉴도 내보내지 않는다.
    //
    // 권한을 모르는 동안 전체 메뉴를 그리면 접근할 수 없는 기능이 잠깐 보였다가 사라진다.
    // 데이터가 새는 것은 아니지만(클릭해도 가드와 API 가 막는다) 사용자에게는 "있었는데 없어진" 기능이라
    // 첫인상이 나쁘고 문의가 생긴다. 비었다가 채워지는 편이 낫다.
    if (!session?.authenticated) return [];

    // 관리자는 전 메뉴를 본다. 전체 메뉴 키의 우주가 여기(menuConfig)에 있으므로 이 판단도 여기서 한다 —
    // 서버의 visibleMenuKeys 는 비관리자용 목록만 담고 관리자에게는 비어 온다.
    const isAdmin = isAdministrator(session.user?.userType);
    const allowedKeys = new Set(session.visibleMenuKeys);
    // 관리자는 전 메뉴를 본다. 그 외에는 서버가 준 visibleMenuKeys(역할 그랜트에서 canView 인 것)만 본다 —
    // 부서 소속이 있으면 서버가 기본 조회를 담아 내려주므로(`DepartmentBaseline`) 여기서 따로 열지 않는다.
    // 탭으로 합친 화면(주간업무·월간업무)은 자기 키가 아니라 원래 화면들의 키로 열린다(`visibleWhenAnyOf`).
    //
    // ★ <b>인사자료 셋도 관리자에게는 그대로 보인다.</b> 관리자 전권이 닿지 않는 것은 맞지만
    //   (`ADMIN_BYPASS_EXCLUDED` · 백엔드 `HrDataAccess`), 그 판정으로 사이드바에서 <b>지워 버리면</b>
    //   어제까지 쓰던 메뉴가 예고 없이 없어진 것으로 읽힌다 — 사라진 메뉴는 사유를 말할 자리조차 없다.
    //   막는 자리는 라우트 가드 하나면 되고(`resolveAccess`), 거기서는 「무엇이 왜 막혔는지」를 말할 수 있다.
    // 아직 열지 않은 메뉴(오픈 게이트 · V153)는 <b>그룹까지</b> 지운다. 서버가 이미 visibleMenuKeys
    // 에서 빼 주지만 그룹은 권한 키를 갖지 않아 그 필터에 걸리지 않는다 — 자료실·자산관리처럼
    // 통째로 닫은 묶음이 「눌러도 아무것도 없는 줄」로 남는다. 관리자에게는 빈 집합이 온다.
    const closedKeys = closedKeysFor(session);
    return mergeMenuConfig(menuSettings)
        .filter((item) => !closedKeys.has(item.menuKey))
        .filter((item) => isAdmin || item.itemType === 'group'
            || menuKeysOf(item).some((key) => allowedKeys.has(key)))
        // 바깥 주소는 <b>설정이 이긴다</b>. 사이드바·검색·홈 위젯이 이 트리 하나를 보므로 여기서
        // 한 번 갈아 끼우면 세 곳이 함께 따라온다 — 화면마다 상수를 다시 읽으면 언젠가 하나가 어긋난다.
        .map((item) => {
          const code = MENU_EXTERNAL_LINKS[item.menuKey];
          const url = code ? externalLinkUrls[code] : undefined;
          return url ? { ...item, href: url } : item;
        });
  }, [externalLinkUrls, menuSettings, session]);

  /**
   * 현장 소속이면 주간업무·월간업무 안에서 <b>현장이 본사보다 앞</b>이다(2026-09-02).
   *
   * <p>순서만 바뀐다. 무엇이 보이는지는 위 권한 필터가 이미 정했다.
   */
  const siteFirst = resolveSiteFirst(session);

  const childrenByGroup = useMemo(() => {
    const groups = new Map<string, MenuConfigItem[]>();
    menuConfig
      .filter((item) => item.groupKey)
      .forEach((item) => {
        const key = item.groupKey as string;
        groups.set(key, [...(groups.get(key) ?? []), item]);
      });
    /*
      자식이 하나도 안 보이는 그룹은 <b>그 그룹도 사라진다</b> — 「눌러도 아무것도 없는 줄」이
      남지 않게. 아래 `topLevel` 이 같은 판정을 하지만 그쪽은 <b>맨 위 한 겹</b>(`groupKey === null`)
      만 본다. 중첩 그룹(`hr_leave_status` · `asset_requests` · `asset_registry`)은 거기 걸리지
      않고, 그룹이라 권한 필터도 무조건 지나므로 빈 줄로 남았다 — 부서를 안 넣은 신규 입사자의
      사이드바에 「인사관리 › 휴무 관리」 만 서 있던 것이 이것이다(2026-09-08).

      하나를 빼면 그 부모가 비는 일이 있어 더 뺄 것이 없을 때까지 돈다.
    */
    let pruned = true;
    while (pruned) {
      pruned = false;
      groups.forEach((items, key) => {
        const kept = items.filter((item) => item.itemType !== 'group'
            || (groups.get(item.menuKey)?.length ?? 0) > 0);
        if (kept.length !== items.length) {
          groups.set(key, kept);
          pruned = true;
        }
      });
    }
    groups.forEach((items, key) => {
      const sorted = [...items].sort(bySortOrder);
      // ★ 관리자가 메뉴 관리에서 매긴 순서보다 <b>이것이 뒤에 온다</b>. 두 그룹뿐이고, 개인의
      //   소속에 따라 달라지는 값이라 전사 설정으로는 표현할 수 없기 때문이다.
      const order = siteFirst ? SITE_FIRST_MENU_ORDER[key] : undefined;
      groups.set(key, order ? orderByKeys(sorted, (item) => item.menuKey, order) : sorted);
    });
    return groups;
  }, [menuConfig, siteFirst]);

  const childrenOf = useMemo(
    () => (menuKey: string) => childrenByGroup.get(menuKey) ?? [],
    [childrenByGroup],
  );

  const topLevel = useMemo(
    () =>
      menuConfig
        .filter((item) => item.groupKey === null)
        // 권한 필터로 자식이 모두 사라진 빈 그룹은 감춘다. 안쪽 중첩 그룹은 위에서 이미 접혔으므로
        // 여기서는 맨 위 한 겹만 보면 된다.
        .filter((item) => item.itemType !== 'group' || (childrenByGroup.get(item.menuKey)?.length ?? 0) > 0)
        .sort(bySortOrder),
    [childrenByGroup, menuConfig],
  );

  /** 항목 키 → 그 항목이 대신 들여다봐야 하는 키들(탭으로 합친 원래 화면들). */
  const aliasesOf = useMemo(
    () => new Map(menuConfig.map((item) => [item.menuKey, menuKeysOf(item)])),
    [menuConfig],
  );

  const hasBadge = useMemo(() => {
    const cache = new Map<string, boolean>();

    const resolve = (menuKey: string): boolean => {
      const cached = cache.get(menuKey);
      if (cached !== undefined) return cached;

      // 순환 참조가 있어도 무한 재귀에 빠지지 않도록 방문 표시를 먼저 심는다.
      cache.set(menuKey, false);
      // 활동은 원래 화면의 키로 쌓인다(`meetings_hq` …). 탭으로 합친 항목이 자기 키만 보면
      // 그 아래에서 무슨 일이 있어도 배지가 뜨지 않는다.
      const self = (aliasesOf.get(menuKey) ?? [menuKey]).some((key) => isRecent(menuActivity?.[key], now));
      const result = self || (childrenByGroup.get(menuKey) ?? []).some((child) => resolve(child.menuKey));
      cache.set(menuKey, result);
      return result;
    };

    return resolve;
  }, [aliasesOf, childrenByGroup, menuActivity, now]);

  // 모든 링크의 href. 접두어(prefix) 매칭이 겹칠 때 "더 구체적인 링크가 이긴다"를 판정하는 데 쓴다.
  const linkHrefs = useMemo(
    () => menuConfig.filter((item) => item.href).map((item) => item.href as string),
    [menuConfig],
  );

  const isActive = useMemo(() => {
    const resolve = (item: MenuConfigItem, pathname: string): boolean => {
      // 자식부터 본다. 그룹만이 아니라 <b>링크도 자식을 가진다</b> — 주간업무(`/meetings/weekly`)는
      // 자기 주소로는 `/meetings/hq` 와 접두어가 겹치지 않아서, 자식을 보지 않으면 v1 주소로 들어온 사람에게
      // 사이드바에서 아무 항목도 켜지지 않는다.
      if ((childrenByGroup.get(item.menuKey) ?? []).some((child) => resolve(child, pathname))) return true;
      if (!item.href) return false;
      if (pathname === item.href) return true;
      // 접두어 매칭(하위 경로)은, 이 경로에 더 딱 맞는(더 긴) 다른 링크가 있으면 양보한다.
      // 예: '/docs/viewer' 는 '자료 조회(/docs)' 가 아니라 '도면 뷰어(/docs/viewer)' 만 활성화한다.
      // 반면 메뉴에 없는 하위 화면('/docs/s3'·'/docs/nas')은 여전히 '자료 조회(/docs)' 를 활성화한다.
      if (!pathname.startsWith(item.href + '/')) return false;
      const href = item.href;
      return !linkHrefs.some((other) => other.length > href.length && (pathname === other || pathname.startsWith(other + '/')));
    };

    return resolve;
  }, [childrenByGroup, linkHrefs]);

  return { topLevel, childrenOf, hasBadge, isActive };
}

function bySortOrder(a: MenuConfigItem, b: MenuConfigItem): number {
  // sortOrder 가 겹치면 기본 골격의 배열 순서를 따르도록 안정 정렬에 맡긴다.
  return a.sortOrder - b.sortOrder;
}
